// Threat-model findings API client. Mirrors the conventions of
// lib/dataDictionaryFindingsApi.ts so the surface stays consistent
// across the resolver-related panels.

import type { ThreatModelFindingsResponse } from '@/types/threatModelFindings'

const API_BASE = '/api'

function getOrCreateClientId(): string {
  const KEY = 'ai_alm.client_id'
  try {
    let id = window.localStorage.getItem(KEY)
    if (!id) {
      id = `c_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`
      window.localStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return 'c_anonymous'
  }
}

function newRequestId(): string {
  return `r_${Math.random().toString(36).slice(2, 10)}`
}

async function jsonFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      'X-Client-Id': getOrCreateClientId(),
      'X-Request-Id': newRequestId(),
      ...(init?.headers ?? {}),
    },
  })
  if (!response.ok) {
    let detail = ''
    try {
      const envelope = await response.json()
      detail =
        envelope?.error?.message ??
        envelope?.detail ??
        response.statusText ??
        `HTTP ${response.status}`
    } catch {
      detail = response.statusText || `HTTP ${response.status}`
    }
    throw new Error(detail)
  }
  return (await response.json()) as T
}

export function getThreatModelFindings(
  projectId: string,
  runId: number,
): Promise<ThreatModelFindingsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/threat-model-findings`,
  )
}

/**
 * G3.7 "Regenerate" — re-run the Security Architect to author the
 * missing threat-model asset(s), then return the refreshed findings.
 * One LLM call server-side (~30–60 s). ``assetNames`` empty → every
 * currently-missing mandatory asset.
 */
export function regenerateThreatModel(
  projectId: string,
  runId: number,
  assetNames: string[] = [],
): Promise<ThreatModelFindingsResponse> {
  return jsonFetch(
    `/projects/${projectId}/runs/${runId}/regenerate/threat-model`,
    { method: 'POST', body: JSON.stringify({ asset_names: assetNames }) },
  )
}
